export interface LayoutAsset {
    width: number
    height: number
}

export interface LayoutOptions {
    maxRowWidth?: number
    gap?: number
    origin?: { x: number; y: number }
}

export interface LayoutResult {
    x: number
    y: number
}

const DEFAULT_MAX_ROW_WIDTH = 1600
const DEFAULT_GAP = 48
const DEFAULT_ORIGIN = { x: 0, y: 0 }

/**
 * Image 节点的显示短边最小尺寸，与 `image-node-config.getImagePreviewDisplaySize` 一致。
 * 布局估算必须与节点实际渲染尺寸匹配，否则会产生重叠。
 */
const IMAGE_NODE_MIN_SIZE = 300

/**
 * 估算素材在 Image 节点中的显示尺寸，用于流式排版。
 *
 * 计算规则与 `getImagePreviewDisplaySize` 对齐（已知宽高时，短边 = IMAGE_NODE_MIN_SIZE）：
 * - 宽 ≤ 高（竖图/方图）：宽 = 300，高等比缩放
 * - 宽 > 高（横图）    ：高 = 300，宽等比缩放
 * - 无宽高             ：300 × 300
 */
export function estimateNodeSize(asset: { width?: number | string; height?: number | string }): LayoutAsset {
    const rawWidth = Number(asset.width) || 0
    const rawHeight = Number(asset.height) || 0

    if (rawWidth <= 0 || rawHeight <= 0) {
        return { width: IMAGE_NODE_MIN_SIZE, height: IMAGE_NODE_MIN_SIZE }
    }

    if (rawWidth <= rawHeight) {
        // 竖图或方图：宽度为短边
        const displayWidth = IMAGE_NODE_MIN_SIZE
        const displayHeight = Math.round((IMAGE_NODE_MIN_SIZE * rawHeight) / rawWidth)
        return { width: displayWidth, height: displayHeight }
    }

    // 横图：高度为短边
    const displayHeight = IMAGE_NODE_MIN_SIZE
    const displayWidth = Math.round((IMAGE_NODE_MIN_SIZE * rawWidth) / rawHeight)
    return { width: displayWidth, height: displayHeight }
}

export function layoutAssets(
    assets: LayoutAsset[],
    options: LayoutOptions = {},
): LayoutResult[] {
    const {
        maxRowWidth = DEFAULT_MAX_ROW_WIDTH,
        gap = DEFAULT_GAP,
        origin = DEFAULT_ORIGIN,
    } = options

    let currentX = 0
    let currentY = 0
    let rowHeight = 0
    const positions: LayoutResult[] = []

    for (const asset of assets) {
        if (currentX > 0 && currentX + asset.width > maxRowWidth) {
            currentX = 0
            currentY += rowHeight + gap
            rowHeight = 0
        }

        positions.push({
            x: origin.x + currentX,
            y: origin.y + currentY,
        })

        currentX += asset.width + gap
        rowHeight = Math.max(rowHeight, asset.height)
    }

    return positions
}

/**
 * 垂直单列排版
 */
export function layoutAssetsVertically(
    assets: LayoutAsset[],
    options: LayoutOptions = {},
): LayoutResult[] {
    const {
        gap = DEFAULT_GAP,
        origin = DEFAULT_ORIGIN,
    } = options

    let currentY = 0
    const positions: LayoutResult[] = []

    for (const asset of assets) {
        positions.push({
            x: origin.x,
            y: origin.y + currentY,
        })
        currentY += asset.height + gap
    }

    return positions
}